<script setup>
import {computed, defineProps} from 'vue'
import {notify} from '@kyvg/vue3-notification'
import copyIcon from '@shared-images/icons/button/copy-outline-icon.svg'
import {getPaymentTypeMeta} from '@shared-components/finance/paymentTypeMeta'
import TheQrCode from './TheQrCode.vue'
import {getPaymentQrValue, isCopyableRequisiteLabel} from './paymentMethodPresentation'

const props = defineProps({
  paymentType: {
    type: [String, undefined],
    default: undefined,
  },
  requisites: {
    type: String,
    default: '',
  },
  tag: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const FIELD_LINE = /^(.+?)\s+[-:]\s+(.+)$/

const meta = computed(() => getPaymentTypeMeta(props.paymentType))
const qrImage = computed(() => meta.value?.qr)
const qrValue = computed(() => getPaymentQrValue(props.paymentType, props.requisites))
const hasQrCode = computed(() => !!(qrImage.value || qrValue.value))

const copyRequisiteValue = async value => {
  try {
    await navigator.clipboard.writeText(value)
    notify({type: 'success', text: 'Copied to clipboard'})
  } catch {
    notify({type: 'error', text: 'Failed to copy to clipboard'})
  }
}

const requisiteRows = computed(() => {
  const rows = (props.requisites || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const match = line.match(FIELD_LINE)

      if (!match) {
        return {text: line}
      }

      const label = match[1].trim()

      return {label, value: match[2].trim(), copyable: isCopyableRequisiteLabel(label)}
    })

  if (props.tag && !hasQrCode.value) {
    rows.push({label: props.tag.label, value: props.tag.value, copyable: true})
  }

  return rows
})
</script>

<template>
  <div v-if="paymentType" class="payment-method-details">
    <div class="payment-method-details__panel">
      <p v-if="meta?.hint" class="payment-method-details__note">{{ meta.hint }}</p>

      <div v-if="loading" class="payment-method-details__loading">Loading…</div>

      <div v-else class="payment-method-details__content">
        <div v-if="hasQrCode" class="payment-method-details__qr-block">
          <div class="payment-method-details__qr-card">
            <img v-if="qrImage" :src="qrImage" alt="Payment QR code" class="payment-method-details__qr-image" />
            <the-qr-code v-else :value="qrValue" />
            <span v-if="tag" class="payment-method-details__tag rounded-pill">{{ tag.value }}</span>
          </div>
          <p class="payment-method-details__hint">Scan the QR code to make your payment</p>
        </div>

        <div v-if="hasQrCode && requisiteRows.length" class="payment-method-details__divider"></div>

        <div v-if="requisiteRows.length" class="payment-method-details__grid">
          <template v-for="(row, index) in requisiteRows" :key="index">
            <template v-if="row.label">
              <span class="payment-method-details__label">{{ row.label }}</span>
              <span class="payment-method-details__value">
                {{ row.value }}
                <span
                  v-if="row.copyable"
                  class="clickable-icon"
                  v-tooltip="'Copy to clipboard'"
                  @click="copyRequisiteValue(row.value)"
                >
                  <img :src="copyIcon" alt="copy" />
                </span>
              </span>
            </template>
            <span v-else class="payment-method-details__line">{{ row.text }}</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.payment-method-details__panel {
  background: #f8f9fa;
  border-radius: toRem(8);
  padding: toRem(32);
}

.payment-method-details__note {
  margin: 0 0 toRem(16);
  font-size: toRem(13);
  line-height: 1.5;
  color: $text-secondary;
}

.payment-method-details__loading {
  font-size: toRem(13);
  color: $text-muted;
}

.payment-method-details__content {
  display: flex;
  align-items: center;
  gap: toRem(32);
}

.payment-method-details__qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  gap: toRem(16);
}

.payment-method-details__qr-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: toRem(16);
  padding: toRem(10) toRem(30) toRem(16);
  background: #fff;
  border-radius: toRem(8);
}

.payment-method-details__qr-image {
  display: block;
  width: toRem(140);
  height: toRem(140);
}

.payment-method-details__tag {
  padding: toRem(4) toRem(12);
  background: #f3f4f6;
  font-size: toRem(13);
  font-weight: 600;
  line-height: 1.4;
  color: $text-dark;
}

.payment-method-details__hint {
  margin: 0;
  font-size: toRem(13);
  line-height: 1.5;
  color: $text-secondary;
  text-align: center;
}

.payment-method-details__divider {
  align-self: stretch;
  width: toRem(1);
  background: $secondary-grey;
}

.payment-method-details__grid {
  display: grid;
  flex-grow: 1;
  grid-template-columns: auto 1fr;
  column-gap: toRem(24);
  row-gap: toRem(8);
  font-size: toRem(13);
  line-height: 1.5;
}

.payment-method-details__label {
  color: $text-muted;
}

.payment-method-details__value {
  display: flex;
  align-items: center;
  gap: toRem(6);
  font-weight: 600;
  color: $black;
}

.payment-method-details__line {
  grid-column: 1 / -1;
  color: $text-dark;
}
</style>
